/**
 * Assessments (SOP) API - Client-Side Service
 * 
 * All assessment database operations go through server-side Express API.
 * Client-side code MUST NOT access the database directly.
 */
import type { Assessment } from '@/lib/types';
import { apiFetch, pollEndpoint } from '@/lib/api-client';

// Response types
interface CreateAssessmentResponse {
  success: boolean;
  assessmentId: string;
  message: string;
}

interface UpdateResponse {
  success: boolean;
  message: string;
}

/**
 * Create a new assessment for a client
 */
export async function createAssessment(
  clientId: string,
  assessment: Omit<Assessment, 'id'>
): Promise<string> {
  const response = await apiFetch<CreateAssessmentResponse>(
    `/clients/${clientId}/assessments`,
    {
      method: 'POST',
      body: JSON.stringify(assessment),
    }
  );
  return response.assessmentId;
}

/**
 * Get a single assessment by ID
 */
export async function getAssessment(
  clientId: string,
  assessmentId: string
): Promise<Assessment | null> {
  try {
    const assessment = await apiFetch<Assessment>(
      `/clients/${clientId}/assessments/${assessmentId}`
    );
    return assessment;
  } catch (error: any) {
    if (error.message?.includes('not-found') || error.message?.includes('404')) {
      return null;
    }
    throw error;
  }
}

/**
 * Get all assessments for a client
 */
export async function getClientAssessments(clientId: string): Promise<Assessment[]> {
  return apiFetch<Assessment[]>(`/clients/${clientId}/assessments`);
}

/**
 * Update an assessment
 */
export async function updateAssessment(
  clientId: string,
  assessmentId: string,
  updates: Partial<Assessment>
): Promise<void> {
  await apiFetch<UpdateResponse>(
    `/clients/${clientId}/assessments/${assessmentId}`,
    {
      method: 'PATCH',
      body: JSON.stringify(updates),
    }
  );
}

/**
 * Delete an assessment
 */
export async function deleteAssessment(
  clientId: string,
  assessmentId: string
): Promise<void> {
  await apiFetch<UpdateResponse>(
    `/clients/${clientId}/assessments/${assessmentId}`,
    {
      method: 'DELETE',
    }
  );
}

/**
 * Subscribe to assessments with polling for real-time updates
 * Returns cleanup function to stop polling
 */
export function subscribeToClientAssessments(
  clientId: string,
  callback: (assessments: Assessment[]) => void,
  intervalMs: number = 5000,
  onError?: (error: Error) => void
): () => void {
  return pollEndpoint<Assessment[]>(
    `/clients/${clientId}/assessments`,
    callback,
    intervalMs,
    onError
  );
}
